import React from 'react';
import { Button } from '@charitystack/shared';
import { getCtaFrequency, getFormCheckoutButtonLabel } from '../Util/formPrimaryCtaUtils';
import { useDisplayCurrency } from '../currency/DisplayCurrencyProvider';

// Form type to ID segment mapping
const formTypeToIdSegment = {
  STANDARD: 'standard',
  PRODUCT: 'product',
  CROWDFUNDING: 'crowdfunding',
  EVENT: 'ticket',
};

/**
 * FormActionButtons - Reusable checkout/submit button component for donation forms
 *
 * The label states what the button does (Paper 00 Decisions 4NE-0, CON-571):
 * - STANDARD/PRODUCT/CROWDFUNDING: "Give $50" | "Give $25 monthly" | "Add to cart" | "Next"
 * - EVENT: "Get 2 tickets · $250" ("Get tickets" before a pick)
 * See getFormCheckoutButtonLabel (Util/formPrimaryCtaUtils).
 *
 * (brandColors / primaryColor are still passed by callers but no longer read:
 *  the CTA paints from the brand role tokens on its scope.)
 * @param {string} elementID - Element ID for button ID generation
 * @param {function} onClick - Click handler for primary button
 * @param {string} formType - Form type: 'STANDARD' | 'PRODUCT' | 'CROWDFUNDING' | 'EVENT'
 * @param {boolean} addToCart - Show "Add to cart" instead of "Give $X" (donation forms only)
 * @param {number|string} amount - What the button will charge (the ticket total on events)
 * @param {number} ticketCount - Tickets picked (events only)
 * @param {string|null|undefined} buttonLabel - When set (e.g. "Next"), overrides default CTA text.
 *   Prefer getPreCheckoutStepPrimaryButtonLabel (Util/formPrimaryCtaUtils) on Standard /
 *   Crowdfunding start pages so wording matches handleButtonClick (custom inputs, monthly bump).
 * @param {Array} frequencies - Available frequency options
 * @param {string} frequency - Currently selected frequency
 * @param {boolean} hostedPagePreview - Whether in hosted page preview mode
 * @param {boolean} isSubmitting - When true, disables the button and shows a spinner to prevent double-clicks
 */
function FormActionButtons({
  elementID,
  onClick,
  buttonLabel = null,
  formType = 'STANDARD',
  addToCart = false,
  frequencies = [],
  frequency = '',
  amount,
  ticketCount = 0,
  hostedPagePreview = false,
  secondaryButtonLabel = '',
  onSecondaryButtonClick,
  secondaryButtonId,
  isSubmitting = false,
  paymentsUnavailable = false,
}) {
  const { converted } = useDisplayCurrency();

  // Generate button ID based on form type
  const buttonId = elementID
    ? `${elementID}-CS-${formTypeToIdSegment[formType] || 'standard'}-form-checkout-button`
    : undefined;

  // Determine button text based on form type and state
  const getButtonContent = () => {
    if (buttonLabel) {
      return (
        <span className="flex items-center justify-center gap-2">
          {buttonLabel}
        </span>
      );
    }

    return (
      <span className="flex items-center justify-center gap-2">
        {getFormCheckoutButtonLabel({
          formType,
          addToCart,
          amount,
          frequency: getCtaFrequency(frequencies, frequency),
          ticketCount,
          converted,
        })}
      </span>
    );
  };

  const showSecondaryButton = secondaryButtonLabel && onSecondaryButtonClick;
  // Back and secondary actions are text-secondary, never brand (SPEC §C);
  // `.form-back-button` paints them.
  const handlePrimaryClick = (event) => {
    if (hostedPagePreview || paymentsUnavailable) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    onClick?.(event);
  };

  return (
    <div className="mt-auto flex flex-col gap-2 form-action-buttons">
      {paymentsUnavailable && (
        // Paper 6PG-0: 14/20 regular, secondary, centred, 12 above the button.
        <p role="status" className="pb-[var(--cs-space-1)] text-center font-sans text-[length:var(--cs-text-sm)] font-normal leading-[var(--cs-leading-20)] text-[color:var(--cs-text-secondary)]">
          Payments are temporarily unavailable. Please try again later.
        </p>
      )}
      <div className="relative overflow-hidden">
        {/* The donate call-to-action. Its fill, label, hover and pressed paint
            are the brand ROLE tokens the BrandColorProvider writes on the form's
            scope (the `cta` variant of the shared Button, CON-514). */}
        <Button
          type="button"
          family="cta"
          variant="cta"
          size="cta"
          className="dynamic-button-text"
          onClick={handlePrimaryClick}
          disabled={isSubmitting || paymentsUnavailable}
          id={buttonId}
        >
          {isSubmitting ? (
            <>
              <span
                className="inline-block size-[var(--cs-icon)] animate-spin rounded-full border-2 border-current border-t-transparent"
                role="status"
                aria-label="Submitting"
              />
              <span aria-hidden="true">Processing</span>
            </>
          ) : (
            getButtonContent()
          )}
        </Button>
      </div>

      {showSecondaryButton && (
        <button
          type="button"
          className="form-back-button"
          onClick={onSecondaryButtonClick}
          id={secondaryButtonId}
        >
          {secondaryButtonLabel}
        </button>
      )}

      {/* The Back to the detail page lives in the first step's title strip
          (Util/detailPageBack.js), not here. */}
    </div>
  );
}

export default FormActionButtons;
